/**
 * CopyableTags Component
 *
 * A clickable tag icon that copies tag values to clipboard.
 * Follows the same pattern as CopyableId, CopyableMember, CopyableSystem, CopyableStatus.
 */

import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { copyToClipboard, useTooltipPosition, getCopyIndicatorIcon, getCheckmarkIcon } from './utils';
import {
  CopyableValueWrapper,
  CopyableValueTrigger,
  PortalTooltipBubble,
  TooltipStack,
  TooltipRow,
  TooltipLabel,
  TooltipHint,
  TooltipMuted,
  EmptyStateBadge
} from './styles';

interface CopyableTagsProps {
  tags: string[];
  onCopy?: (value: string) => void;
  className?: string;
}

// Tags-specific trigger with grey color scheme (matches other icons)
const TagsTrigger = styled(CopyableValueTrigger)<{ $copied?: boolean; $empty?: boolean }>`
  background: ${props => {
    if (props.$copied) return 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)';
    if (props.$empty) return '#f8fafc';
    return '#f1f5f9';
  }};

  color: ${props => {
    if (props.$copied) return '#16a34a';
    if (props.$empty) return '#cbd5e1';
    return '#64748b';
  }};

  border: 1px solid ${props => {
    if (props.$copied) return '#86efac';
    if (props.$empty) return '#e2e8f0';
    return 'rgba(0, 0, 0, 0.04)';
  }};

  cursor: ${props => props.$empty ? 'default' : 'pointer'};

  &:hover {
    ${props => !props.$empty && !props.$copied && `
      background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
      color: #475569;
    `}
  }
`;

// Tag badge for tooltip display (grey scheme)
const TagBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.4rem;
  background: rgba(100, 116, 139, 0.2);
  color: #94a3b8;
  border-radius: 0.25rem;
  font-size: 0.65rem;
  font-weight: 500;
  white-space: nowrap;
`;

const TagsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.25rem;
`;

const getTagIcon = (): React.ReactNode => (
  <svg className="main-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const getEmptyTagIcon = (): React.ReactNode => (
  <svg className="main-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.4">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

export const CopyableTags: React.FC<CopyableTagsProps> = ({
  tags,
  onCopy,
  className,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [copied, setCopied] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  const isEmpty = !tags || tags.length === 0;
  const tagsString = tags?.join(', ') || '';

  const mainTooltipPosition = useTooltipPosition(triggerRef, isHovered && !copied && !isEmpty, 180, 80, 10, 15);
  const successTooltipPosition = useTooltipPosition(triggerRef, copied, 80, 40, 10, 15);
  const emptyTooltipPosition = useTooltipPosition(triggerRef, isHovered && isEmpty, 120, 50, 10, 15);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isEmpty) return;

    const success = await copyToClipboard(tagsString);
    if (success) {
      setCopied(true);
      onCopy?.(tagsString);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  // Handle mouse leave - also reset copied state
  const handleMouseLeave = () => {
    setIsHovered(false);
    setCopied(false);
  };

  // Empty state - show muted icon with wrapper for consistent hover area
  if (isEmpty) {
    return (
      <CopyableValueWrapper
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={handleMouseLeave}
        className={className}
      >
        <TagsTrigger
          ref={triggerRef}
          $empty={true}
        >
          {getEmptyTagIcon()}
        </TagsTrigger>

        {/* Empty tooltip */}
        {ReactDOM.createPortal(
          <PortalTooltipBubble
            $visible={isHovered}
            $position={emptyTooltipPosition}
            $maxWidth={120}
          >
            <TooltipRow $justify="center">
              <TooltipMuted>no tags</TooltipMuted>
            </TooltipRow>
          </PortalTooltipBubble>,
          document.body
        )}
      </CopyableValueWrapper>
    );
  }

  return (
    <CopyableValueWrapper
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      className={className}
    >
      <TagsTrigger
        ref={triggerRef}
        $copied={copied}
        onClick={handleCopy}
      >
        {getTagIcon()}
        <span className="copy-indicator">{getCopyIndicatorIcon()}</span>
      </TagsTrigger>

      {/* Main tooltip - shows on hover */}
      {ReactDOM.createPortal(
        <PortalTooltipBubble
          $visible={isHovered && !copied}
          $position={mainTooltipPosition}
          $maxWidth={180}
        >
          <TooltipStack>
            <TooltipRow $justify="between">
              <TooltipLabel>tags</TooltipLabel>
              <TooltipHint>
                {getCopyIndicatorIcon()}
                click
              </TooltipHint>
            </TooltipRow>
            <TagsList>
              {tags.map((tag, index) => (
                <TagBadge key={index}>{tag}</TagBadge>
              ))}
            </TagsList>
          </TooltipStack>
        </PortalTooltipBubble>,
        document.body
      )}

      {/* Success tooltip - shows after copy */}
      {ReactDOM.createPortal(
        <PortalTooltipBubble
          $visible={copied}
          $position={successTooltipPosition}
          $variant="success"
          $maxWidth={80}
        >
          <TooltipRow $justify="center">
            <span style={{ width: 12, height: 12, marginRight: 4 }}>{getCheckmarkIcon()}</span>
            copied!
          </TooltipRow>
        </PortalTooltipBubble>,
        document.body
      )}
    </CopyableValueWrapper>
  );
};

export default CopyableTags;
